import { DetailRow } from "@/components/shared/finance/DetailRow";
import { EntityLink } from "@/components/shared/relationships/EntityLink";
import {
  createPeekContent,
  DocumentSummaryContent,
  toLineItemRows,
} from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { useEstimate } from "@/features/estimates/hooks/use-estimates";
import { formatDocumentUid } from "@/lib/format";

function EstimateContent({
  estimate,
}: {
  estimate: NonNullable<ReturnType<typeof useEstimate>["data"]>;
}) {
  const badge =
    estimate.converted_invoice_id != null ? (
      <Badge>Converted</Badge>
    ) : (
      <Badge variant="outline">Open</Badge>
    );
  const extraRow = estimate.converted_invoice_id ? (
    <DetailRow
      label="Converted Invoice"
      value={
        <EntityLink entity={{ id: estimate.converted_invoice_id, type: "invoice" }}>
          View invoice
        </EntityLink>
      }
    />
  ) : null;

  return (
    <DocumentSummaryContent
      data={{
        accountId: estimate.account_id,
        accountLabel: "Account",
        accountName: estimate.gl_accounts?.name,
        badge,
        balance: estimate.balance,
        date: estimate.date,
        extraRow,
        lineItemsEmptyMessage: "No line items found",
        lines: toLineItemRows(estimate.lines),
        linesLabel: "Line Items",
        title: formatDocumentUid(estimate.uid, estimate.id),
        totalAmount: estimate.total_amount,
        totalPaid: estimate.total_paid,
      }}
    />
  );
}

/** Estimate quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const EstimatePeekContent = createPeekContent({
  emptyMessage: "No estimate found",
  render: (estimate) => <EstimateContent estimate={estimate} />,
  useDetail: useEstimate,
});

registerPeekType("estimate", EstimatePeekContent);
